/**
 * White-Label Provider Component
 * 
 * Applies white-label branding when:
 * 1. Family has whiteLabelEnabled, OR
 * 2. Member has partnerId (partner branding)
 * Wraps the app to apply branding automatically.
 */

import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useCurrentFamily } from '../../hooks/useFamily';
import { applyWhiteLabelBranding, removeWhiteLabelBranding } from '../../utils/whiteLabel';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';

const WhiteLabelProvider = ({ children }) => {
  const location = useLocation();
  const isPartnerRoute = location.pathname.startsWith('/partner');
  
  // Only fetch family data if not on partner routes (partners don't have families)
  const { data: family } = useCurrentFamily({ enabled: !isPartnerRoute });
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [partnerBranding, setPartnerBranding] = useState(null);
  const [loadingPartner, setLoadingPartner] = useState(true);

  // Fetch partner branding - check if user is a partner first
  useEffect(() => {
    const fetchPartnerBranding = async () => {
      if (!isAuthenticated) {
        setLoadingPartner(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        
        // First check if user is a partner using check-status endpoint (doesn't require role)
        try {
          const checkRes = await fetch(`${API_BASE_URL}/api/partner/check-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            if (checkData.isPartner) {
              // User is a partner - try to get branding from partner API
              try {
                const partnerRes = await fetch(`${API_BASE_URL}/api/partner/me`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (partnerRes.ok) {
                  const partnerData = await partnerRes.json();
                  if (partnerData.partner?.branding) {
                    console.log('✅ Applying partner branding from partner API:', partnerData.partner.branding);
                    setPartnerBranding(partnerData.partner.branding);
                  } else {
                    setPartnerBranding(null);
                  }
                } else {
                  // Partner API might require role - skip for now, branding can be handled on partner routes
                  setPartnerBranding(null);
                }
              } catch (partnerError) {
                console.log('Could not fetch partner branding from partner API');
                setPartnerBranding(null);
              }
              setLoadingPartner(false);
              return; // Don't try to fetch member data for partners
            }
          }
        } catch (checkError) {
          // Check-status failed - not a partner or API error, continue to member check
          console.log('Partner check failed, checking if member instead');
        }
        
        // Not a partner (or check failed) - try to get branding from member data
        try {
          const memberRes = await fetch(`${API_BASE_URL}/api/members/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (memberRes.ok) {
            const member = await memberRes.json();
            
            // Partner branding is included in member response if member has partnerId
            if (member.partnerBranding) {
              setPartnerBranding(member.partnerBranding);
            } else {
              setPartnerBranding(null);
            }
          } else {
            // 404 means not a member either (could be partner or not logged in properly)
            setPartnerBranding(null);
          }
        } catch (memberError) {
          setPartnerBranding(null);
        }
      } catch (error) {
        console.error('Error fetching partner branding:', error);
        setPartnerBranding(null);
      } finally {
        setLoadingPartner(false);
      }
    };

    fetchPartnerBranding();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Apply branding: Partner branding takes priority over family branding
  useEffect(() => {
    if (loadingPartner) return; // Wait for partner check

    // Priority 1: Partner branding (if member has partnerId)
    if (partnerBranding) {
      console.log('🎨 Applying partner branding to UI:', partnerBranding);
      
      // Ensure all branding values are present
      const brandingToApply = {
        primaryColor: partnerBranding.primaryColor,
        secondaryColor: partnerBranding.secondaryColor,
        backgroundColor: partnerBranding.backgroundColor,
        textColor: partnerBranding.textColor,
        accentColor: partnerBranding.accentColor,
        logo: partnerBranding.logoUrl || partnerBranding.logo
      };
      
      console.log('🎨 Branding values to apply:', brandingToApply);
      
      // Apply branding immediately
      applyWhiteLabelBranding(brandingToApply);
      
      // Also apply after a short delay to catch any late-rendering elements
      setTimeout(() => {
        applyWhiteLabelBranding(brandingToApply);
        console.log('✅ Branding re-applied after delay');
      }, 500);
      
      return;
    }

    // Priority 2: Family branding (if enabled)
    if (family?.whiteLabelEnabled && family?.whiteLabelBranding) {
      applyWhiteLabelBranding(family.whiteLabelBranding);
      return;
    }

    // No branding - remove any existing
    removeWhiteLabelBranding();
  }, [family, partnerBranding, loadingPartner]);

  return <>{children}</>;
};

export default WhiteLabelProvider;

